import { pickBy } from 'lodash'

// STY-730: values are URL-encoded so an email with a "+" (jane+smp@gmail.com)
// still matches; unencoded, the "+" became a space and the list came back
// empty.
export function buildSubmissionsQuery (filters, notFilters) {
  const query = Object.keys(pickBy(filters)).map((key) => `${key}=${encodeURIComponent(filters[key])}`).join('&')
  const notQuery = Object.keys(pickBy(notFilters)).map((key) => `${key}!=${encodeURIComponent(notFilters[key])}`).join('&')
  return [query, notQuery].filter(Boolean).join('&')
}

// STY-725: for submission_create_failed. Never shown to the vendor.
function statusOf (error) {
  const status = error && error.response && error.response.status
  return Number.isInteger(status) ? status : undefined
}

export const CREATE_FAILED = "We couldn't create your submission. Please try again in a moment."
export const CREATE_FAILED_AFTER_QUOTA = "We couldn't create your quick submission, and one quick submission credit may have been used. Please email submissions-help@stylemepretty.com and we'll restore it."

/**
 * STY-730: the create chain had no error handling. A failure left the modal
 * stuck with a disabled Create button; a failure in a WordPress follow-up
 * (prompt seed, workshop host) after Django had already created the row left
 * an orphan "started" submission the vendor never saw. Now: if the submission
 * exists, always open it (the page re-seeds missing prompts on load); if it
 * doesn't, report an error the modal can show.
 */
// onResult sees every outcome, and a success BEFORE navigate(): the funnel
// event is recorded while the route is still the dashboard (local Codex review).
export async function runCreateSubmission ({ before, create, followUps, navigate, onResult = () => {} }) {
  const settle = (result) => { try { onResult(result) } catch (e) {} return result }
  // A required step that must succeed before anything is created -- the
  // quick-submission quota decrement. Reserving quota first means a WordPress
  // outage can't be used to create quick submissions without spending quota
  // (Codex P1 on #108; the server doesn't enforce the quota, STY-597).
  if (before) {
    try {
      await before()
    } catch (error) {
      // The decrement isn't idempotent either: it may have committed even though
      // the response was lost, so don't invite an immediate retry that could
      // spend another credit -- say a credit may have been used (Codex P1 round 4).
      return settle({ ok: false, error: CREATE_FAILED_AFTER_QUOTA, step: 'quick_decrement', httpStatus: statusOf(error) })
    }
  }
  // The create POST is NOT idempotent: if Django commits the row but the
  // response is lost, a retry would create a second submission (and spend
  // another credit). So it is attempted exactly once (Codex P1 round 3, #108).
  // Once quota is reserved there is no endpoint to give it back, so a failure
  // tells the vendor exactly what to do. The real fix is an atomic,
  // idempotent server-side create-and-decrement (follow-up).
  let id
  try {
    id = await create()
  } catch (error) {
    return settle({ ok: false, error: before ? CREATE_FAILED_AFTER_QUOTA : CREATE_FAILED, step: 'django', httpStatus: statusOf(error) })
  }
  const results = await Promise.all((followUps(id) || []).map((p) => Promise.resolve(p).then(() => null, (e) => e)))
  const followUpErrors = results.filter(Boolean)
  const result = settle({ ok: true, id, followUpErrors })
  navigate(id)
  return result
}
